import { useEffect, useState } from "react";

const STORAGE_KEY = "majourneys.recentBooking";
const EVENT_NAME = "majourneys:recent-booking";

interface RecentBookingMap {
  [activitySlug: string]: { reference: string; createdAt: number };
}

function readMap(): RecentBookingMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as RecentBookingMap;
  } catch {
    return {};
  }
}

function getRecentBooking(activitySlug: string): string | null {
  const map = readMap();
  return map[activitySlug]?.reference ?? null;
}

/**
 * React hook: returns the most recent booking ref for `activitySlug`, kept in
 * sync with both same-tab updates (via custom event) and cross-tab updates
 * (via the `storage` event).
 */
export function useRecentBooking(activitySlug: string | undefined): string | null {
  const [reference, setReference] = useState<string | null>(() =>
    activitySlug ? getRecentBooking(activitySlug) : null,
  );

  useEffect(() => {
    if (!activitySlug) {
      setReference(null);
      return;
    }
    const slug = activitySlug;
    setReference(getRecentBooking(slug));

    function handleSameTab(e: Event) {
      const custom = e as CustomEvent<{ activitySlug: string; reference: string | null }>;
      if (custom.detail?.activitySlug !== slug) return;
      setReference(custom.detail.reference);
    }
    function handleCrossTab(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setReference(getRecentBooking(slug));
    }

    window.addEventListener(EVENT_NAME, handleSameTab);
    window.addEventListener("storage", handleCrossTab);
    return () => {
      window.removeEventListener(EVENT_NAME, handleSameTab);
      window.removeEventListener("storage", handleCrossTab);
    };
  }, [activitySlug]);

  return reference;
}
